import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { countOpenThreadsByTargetId } from "components/dashboard/body/projects/panels/schedule/comments/scheduleCommentAnchors";
import { parseDocumentCommentsListResponse } from "features/script-editor/parseDocumentCommentsListResponse";
import { scriptBreakdownPath } from "features/script-editor/lib/scene-link";
import {
  invokeProjectCommentsScheduleScroll,
  invokeProjectCommentsScreenplayGoTo,
  setProjectCommentsScheduleScrollHandler,
  setProjectCommentsScreenplayGoToHandler,
} from "features/project-comments/projectCommentsBridge";
import {
  getProjectCommentsShellState,
  patchProjectCommentsShellState,
  resetProjectCommentsShellState,
} from "features/project-comments/projectCommentsShellState";
import {
  DEFAULT_PROJECT_COMMENTS_SHELL_STATE,
  type OpenProjectCommentsOptions,
  type ProjectCommentsShellState,
  type ScheduleCommentScrollHandler,
  type ScreenplayCommentGoToHandler,
} from "features/project-comments/projectComments.types";
import { useAllDocumentComments } from "hooks/project/useAllDocumentComments";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace";
import { projectCommentsKeys } from "queries/projectComments.keys";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { releaseRadixPointerLock } from "utils/releaseRadixPointerLock";

export type { OpenProjectCommentsOptions };

function useProjectCommentsScope() {
  const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
  const projectIdStr = projectId != null ? String(projectId) : "";
  const documentIdStr = documentId != null ? String(documentId) : "";

  return {
    projectId: projectIdStr,
    documentId: documentIdStr,
    enabled: hasValidIds,
  };
}

export function useProjectCommentsShellState(
  projectId: string,
  documentId: string,
  enabled: boolean
): ProjectCommentsShellState {
  const queryClient = useQueryClient();
  const queryKey = projectCommentsKeys.shell(projectId, documentId);

  const { data } = useQuery<ProjectCommentsShellState>(
    queryKey,
    () => getProjectCommentsShellState(queryClient, projectId, documentId),
    {
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
      cacheTime: Number.POSITIVE_INFINITY,
    }
  );

  if (!enabled) {
    return DEFAULT_PROJECT_COMMENTS_SHELL_STATE;
  }

  return data ?? DEFAULT_PROJECT_COMMENTS_SHELL_STATE;
}

export function useProjectCommentsShellActions() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceIdentifiers();
  const { projectId, documentId, enabled } = useProjectCommentsScope();

  const navigateToScheduleRoadmap = useCallback(() => {
    if (!workspaceId || !projectId) return;
    navigate(`/workspaces/${workspaceId}/projects/${projectId}/schedule/roadmap`);
  }, [navigate, projectId, workspaceId]);

  const navigateToScriptBreakdown = useCallback(
    (sceneId?: number | null) => {
      if (!workspaceId || !projectId) return;
      navigate(scriptBreakdownPath(workspaceId, projectId, sceneId));
    },
    [navigate, projectId, workspaceId]
  );

  const openComments = useCallback(
    (options?: OpenProjectCommentsOptions) => {
      if (!enabled) return;

      patchProjectCommentsShellState(queryClient, projectId, documentId, (prev) => ({
        ...prev,
        isOpen: true,
        pendingScheduleRootAnchor: options?.anchorTarget ?? prev.pendingScheduleRootAnchor,
        scrollToThreadRequest: options?.scrollToThreadId
          ? {
              rootCommentId: options.scrollToThreadId,
              nonce: Date.now(),
            }
          : prev.scrollToThreadRequest,
      }));
    },
    [documentId, enabled, projectId, queryClient]
  );

  const closeComments = useCallback(() => {
    if (!enabled) return;

    patchProjectCommentsShellState(queryClient, projectId, documentId, (prev) => ({
      ...prev,
      isOpen: false,
      pendingScheduleRootAnchor: null,
    }));
    releaseRadixPointerLock();
  }, [documentId, enabled, projectId, queryClient]);

  const clearScrollToThreadRequest = useCallback(() => {
    if (!enabled) return;

    patchProjectCommentsShellState(queryClient, projectId, documentId, (prev) => ({
      ...prev,
      scrollToThreadRequest: null,
    }));
  }, [documentId, enabled, projectId, queryClient]);

  const clearPendingScheduleRootAnchor = useCallback(() => {
    if (!enabled) return;

    patchProjectCommentsShellState(queryClient, projectId, documentId, (prev) => ({
      ...prev,
      pendingScheduleRootAnchor: null,
    }));
  }, [documentId, enabled, projectId, queryClient]);

  const setCommentsOpen = useCallback(
    (open: boolean) => {
      if (!enabled) return;

      patchProjectCommentsShellState(queryClient, projectId, documentId, (prev) => ({
        ...prev,
        isOpen: open,
        pendingScheduleRootAnchor: open ? prev.pendingScheduleRootAnchor : null,
      }));
    },
    [documentId, enabled, projectId, queryClient]
  );

  const scrollToScheduleTargetId = useCallback(
    (targetId: string) => {
      if (!enabled) return;

      invokeProjectCommentsScheduleScroll(
        projectId,
        documentId,
        targetId,
        navigateToScheduleRoadmap
      );
    },
    [documentId, enabled, navigateToScheduleRoadmap, projectId]
  );

  const goToScreenplayAnchor = useCallback(
    (paragraphId: number, sceneId?: number | null) => {
      if (!enabled) return;

      closeComments();
      window.requestAnimationFrame(() => {
        releaseRadixPointerLock();
        invokeProjectCommentsScreenplayGoTo(projectId, documentId, { paragraphId, sceneId }, () =>
          navigateToScriptBreakdown(sceneId)
        );
      });
    },
    [closeComments, documentId, enabled, navigateToScriptBreakdown, projectId]
  );

  const goToScheduleAnchor = useCallback(
    (targetId: string) => {
      if (!enabled) return;

      closeComments();
      window.requestAnimationFrame(() => {
        releaseRadixPointerLock();
        scrollToScheduleTargetId(targetId);
      });
    },
    [closeComments, enabled, scrollToScheduleTargetId]
  );

  return {
    projectId,
    documentId,
    enabled,
    openComments,
    closeComments,
    setCommentsOpen,
    clearScrollToThreadRequest,
    clearPendingScheduleRootAnchor,
    scrollToScheduleTargetId,
    goToScreenplayAnchor,
    goToScheduleAnchor,
  };
}

export function useScheduleCommentOpenThreadCounts(
  projectId: string,
  documentId: string,
  enabled: boolean
): Record<string, number> {
  const commentsQuery = useAllDocumentComments(projectId, documentId, enabled);

  return useMemo(() => {
    const scheduleComments = parseDocumentCommentsListResponse(commentsQuery.data).filter(
      (comment) => {
        const anchor = comment.anchors?.[0];
        return anchor?.feature === "schedule";
      }
    );
    return countOpenThreadsByTargetId(scheduleComments);
  }, [commentsQuery.data]);
}

export function useRegisterProjectCommentsScreenplayGoTo(
  handler: ScreenplayCommentGoToHandler | null
) {
  const { projectId, documentId, enabled } = useProjectCommentsScope();

  useEffect(() => {
    if (!enabled) return;

    setProjectCommentsScreenplayGoToHandler(projectId, documentId, handler);
    return () => {
      setProjectCommentsScreenplayGoToHandler(projectId, documentId, null);
    };
  }, [documentId, enabled, handler, projectId]);
}

export function useRegisterProjectCommentsScheduleScroll(
  handler: ScheduleCommentScrollHandler | null
) {
  const { projectId, documentId, enabled } = useProjectCommentsScope();

  useEffect(() => {
    if (!enabled) return;

    setProjectCommentsScheduleScrollHandler(projectId, documentId, handler);
    return () => {
      setProjectCommentsScheduleScrollHandler(projectId, documentId, null);
    };
  }, [documentId, enabled, handler, projectId]);
}

export function useResetProjectCommentsShellOnScopeChange() {
  const queryClient = useQueryClient();
  const { projectId, documentId, enabled } = useProjectCommentsScope();

  useEffect(() => {
    if (enabled) return;
    resetProjectCommentsShellState(queryClient, projectId, documentId);
  }, [documentId, enabled, projectId, queryClient]);
}
